import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  getSlackHealth,
  getAiHealth,
  getSchedulerHealth,
  triggerRollups,
  getOrgSettings,
  updateOrgSettings,
} from '../../api/adminClient';

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState<'config' | 'commands'>('config');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Configure system settings and view Slack command reference</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="flex border-b border-gray-200">
          <button onClick={() => setActiveTab('config')}
            className={clsx('px-6 py-4 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'config' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            Configuration
          </button>
          <button onClick={() => setActiveTab('commands')}
            className={clsx('px-6 py-4 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'commands' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            Slack Commands
          </button>
        </div>
      </div>

      {activeTab === 'config' ? <ConfigurationTab /> : <CommandsTab />}
    </div>
  );
}

// ============================================================================
// CONFIGURATION TAB
// ============================================================================

function ConfigurationTab() {
  return (
    <div className="space-y-6">
      <ServiceStatusCards />
      <DeliveryScheduleSettings />
      <FeatureFlagSettings />
    </div>
  );
}

// ============================================================================
// SERVICE STATUS CARDS
// ============================================================================

function ServiceStatusCards() {
  const { data: scheduler, isLoading: schedulerLoading } = useQuery({
    queryKey: ['settings', 'scheduler'],
    queryFn: getSchedulerHealth,
  });

  const { data: slack, isLoading: slackLoading } = useQuery({
    queryKey: ['settings', 'slack-health'],
    queryFn: getSlackHealth,
  });

  const { data: ai, isLoading: aiLoading } = useQuery({
    queryKey: ['settings', 'ai-health'],
    queryFn: getAiHealth,
  });

  const triggerMutation = useMutation({ mutationFn: triggerRollups });

  const testSlackMutation = useMutation({ mutationFn: getSlackHealth });
  const testAiMutation = useMutation({ mutationFn: getAiHealth });

  return (
    <>
      {/* Scheduler */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Scheduler Status</h2>
          {scheduler?.running ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Running</span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Stopped</span>
          )}
        </div>
        {schedulerLoading ? <p className="text-gray-500">Loading...</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Manager Reminder Schedule</label>
                <p className="mt-1 font-mono text-sm">{scheduler?.schedule || 'N/A'}</p>
                <p className="text-xs text-gray-400 mt-0.5">DMs managers to run /rollup send</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Report Delivery Schedule</label>
                <p className="mt-1 font-mono text-sm">{scheduler?.reportsSchedule || 'N/A'}</p>
                <p className="text-xs text-gray-400 mt-0.5">Delivers rollups to scope-based recipients</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Timezone</label>
                <p className="mt-1">{scheduler?.timezone || 'UTC'}</p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <button onClick={() => triggerMutation.mutate()} disabled={triggerMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {triggerMutation.isPending ? 'Triggering...' : 'Trigger Rollups Now'}
              </button>
              {triggerMutation.isSuccess && <p className="mt-2 text-sm text-green-600">Rollup generation started</p>}
              {triggerMutation.isError && <p className="mt-2 text-sm text-red-600">{(triggerMutation.error as Error).message}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Slack */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Slack Integration</h2>
          {slack?.connected ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Connected</span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Disconnected</span>
          )}
        </div>
        {slackLoading ? <p className="text-gray-500">Loading...</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Bot Name</label>
                <p className="mt-1">{slack?.bot_name || 'Not configured'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Workspace</label>
                <p className="mt-1">{slack?.workspace || 'Not configured'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Bot ID</label>
                <p className="mt-1 font-mono text-sm">{slack?.bot_id || 'N/A'}</p>
              </div>
            </div>
            {slack?.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{slack.error}</div>
            )}
            <div className="pt-4 border-t border-gray-100">
              <button onClick={() => testSlackMutation.mutate()} disabled={testSlackMutation.isPending}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                {testSlackMutation.isPending ? 'Testing...' : 'Test Connection'}
              </button>
              {testSlackMutation.isSuccess && (
                <p className="mt-2 text-sm text-green-600">
                  {testSlackMutation.data?.connected ? `Connected to ${testSlackMutation.data.workspace}` : 'Connection failed'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* AI */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">AI Configuration</h2>
          {ai?.configured ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Not Configured</span>
          )}
        </div>
        {aiLoading ? <p className="text-gray-500">Loading...</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Provider</label>
                <p className="mt-1">{ai?.provider || 'Not configured'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Model</label>
                <p className="mt-1">{ai?.model || 'Not configured'}</p>
              </div>
            </div>
            {ai?.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{ai.error}</div>
            )}
            <div className="pt-4 border-t border-gray-100">
              <button onClick={() => testAiMutation.mutate()} disabled={testAiMutation.isPending}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                {testAiMutation.isPending ? 'Testing...' : 'Test Connection'}
              </button>
              {testAiMutation.isSuccess && (
                <p className="mt-2 text-sm text-green-600">
                  {testAiMutation.data?.configured ? `Connected: ${testAiMutation.data.provider} ${testAiMutation.data.model}` : `Failed: ${testAiMutation.data?.error}`}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// DELIVERY SCHEDULE SETTINGS
// ============================================================================

function DeliveryScheduleSettings() {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ['settings', 'org'],
    queryFn: getOrgSettings,
  });

  // Report Delivery (Sunday by default — when scope-based recipients get rollups)
  const [day, setDay] = useState('');
  const [time, setTime] = useState('');
  // Manager Reminder (Monday by default — when managers get the /rollup send DM)
  const [reminderDay, setReminderDay] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  // Shared
  const [timezone, setTimezone] = useState('');
  const [channelId, setChannelId] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Initialize form from settings
  if (settings && !initialized) {
    setDay(settings.slack.delivery_day);
    setTime(settings.slack.delivery_time);
    setReminderDay(settings.slack.rollup_reminder_day);
    setReminderTime(settings.slack.rollup_reminder_time);
    setTimezone(settings.slack.timezone);
    setChannelId(settings.slack.default_channel_id || '');
    setInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: () => updateOrgSettings({
      slack: {
        delivery_day: day,
        delivery_time: time,
        rollup_reminder_day: reminderDay,
        rollup_reminder_time: reminderTime,
        timezone,
        default_channel_id: channelId || null,
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['scheduler-health'] });
    },
  });

  const hasChanges = settings && (
    day !== settings.slack.delivery_day ||
    time !== settings.slack.delivery_time ||
    reminderDay !== settings.slack.rollup_reminder_day ||
    reminderTime !== settings.slack.rollup_reminder_time ||
    timezone !== settings.slack.timezone ||
    (channelId || null) !== settings.slack.default_channel_id
  );

  const isEnvDefault = (field: string, value: string) => {
    if (!settings) return false;
    if (field === 'timezone') return value === settings.env_defaults.rollup_timezone;
    return false;
  };

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const timezones = [
    'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
    'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
    'Australia/Sydney', 'Pacific/Auckland',
  ];

  const resetForm = () => {
    if (!settings) return;
    setDay(settings.slack.delivery_day);
    setTime(settings.slack.delivery_time);
    setReminderDay(settings.slack.rollup_reminder_day);
    setReminderTime(settings.slack.rollup_reminder_time);
    setTimezone(settings.slack.timezone);
    setChannelId(settings.slack.default_channel_id || '');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Rollup Pipeline Schedules</h2>
      <p className="text-sm text-gray-500 mb-5">
        Two cron jobs run the weekly cycle: the <strong>Manager Reminder</strong> kicks off the lock workflow,
        and the <strong>Report Delivery</strong> sends rollups to scope-based recipients. Both run in the same timezone.
      </p>

      {/* Manager Reminder Schedule */}
      <div className="border-l-4 border-blue-400 pl-4 mb-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">📨 Manager Reminder Schedule</h3>
        <p className="text-xs text-gray-500 mb-3">
          When managers are DM'd to run <code>/powermove rollup send</code> and lock their team's reports
          for the week. Default: Monday 9 AM.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Day</label>
            <select value={reminderDay} onChange={(e) => setReminderDay(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none">
              {days.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Time</label>
            <input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
          </div>
        </div>
      </div>

      {/* Report Delivery Schedule */}
      <div className="border-l-4 border-green-400 pl-4 mb-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">📈 Report Delivery Schedule</h3>
        <p className="text-xs text-gray-500 mb-3">
          When personalized rollups are delivered via Slack DM to scope-based recipients
          (R&amp;D leads, sales). Only locked reports are included. Default: Sunday 9 AM.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Day</label>
            <select value={day} onChange={(e) => setDay(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none">
              {days.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
          </div>
        </div>
      </div>

      {/* Shared: Timezone + Channel */}
      <div className="border-l-4 border-gray-300 pl-4 mb-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">⚙ Shared Settings</h3>
        <p className="text-xs text-gray-500 mb-3">Applied to both schedules above.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
              {isEnvDefault('timezone', timezone) && <span className="ml-1 text-xs text-gray-400">(env default)</span>}
            </label>
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none">
              {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Channel ID</label>
            <input type="text" value={channelId} onChange={(e) => setChannelId(e.target.value)}
              placeholder="C0A565UMBR9"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 outline-none" />
          </div>
        </div>
      </div>

      {hasChanges && (
        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm">
            {saveMutation.isPending ? 'Saving...' : 'Save Schedules'}
          </button>
          <button onClick={resetForm}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
            Reset
          </button>
        </div>
      )}
      {saveMutation.isSuccess && <p className="mt-2 text-sm text-green-600">Schedules saved — scheduler restarted with new values</p>}
      {saveMutation.isError && <p className="mt-2 text-sm text-red-600">{(saveMutation.error as Error).message}</p>}
    </div>
  );
}

// ============================================================================
// FEATURE FLAGS
// ============================================================================

function FeatureFlagSettings() {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ['settings', 'org'],
    queryFn: getOrgSettings,
  });

  const saveMutation = useMutation({
    mutationFn: (features: Record<string, unknown>) => updateOrgSettings({ features }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const [thresholdPct, setThresholdPct] = useState<string>('');
  useEffect(() => {
    if (settings) {
      setThresholdPct(String(Math.round(settings.features.submission_anomaly_threshold * 100)));
    }
  }, [settings]);

  if (!settings) return null;

  const toggleFeature = (key: string, current: boolean) => {
    saveMutation.mutate({ [key]: !current });
  };

  const currentThresholdPct = Math.round(settings.features.submission_anomaly_threshold * 100);
  const parsedThreshold = parseInt(thresholdPct, 10);
  const thresholdValid = !isNaN(parsedThreshold) && parsedThreshold >= 0 && parsedThreshold <= 100;
  const thresholdDirty = thresholdValid && parsedThreshold !== currentThresholdPct;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Feature Flags</h2>
      <div className="space-y-3">
        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
          <div>
            <span className="text-sm font-medium text-gray-700">DevTools</span>
            <p className="text-xs text-gray-500">Enable DevTools tab for report preview, test data generation, and diagnostics</p>
          </div>
          <input type="checkbox" checked={settings.features.devtools_enabled}
            onChange={() => toggleFeature('devtools_enabled', settings.features.devtools_enabled)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        </label>
        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
          <div>
            <span className="text-sm font-medium text-gray-700">Audit Export</span>
            <p className="text-xs text-gray-500">Enable CSV/JSON export from the Audit Trail page</p>
          </div>
          <input type="checkbox" checked={settings.features.audit_export_enabled}
            onChange={() => toggleFeature('audit_export_enabled', settings.features.audit_export_enabled)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        </label>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex-1 pr-4">
            <span className="text-sm font-medium text-gray-700">Submission alert threshold</span>
            <p className="text-xs text-gray-500">
              Trigger a "low submission volume" alert on the Submission Monitor when today's
              count falls below this % of the 4-week same-day average (silent = 0).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={100}
              value={thresholdPct}
              onChange={(e) => setThresholdPct(e.target.value)}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-primary-500 outline-none"
            />
            <span className="text-sm text-gray-500">%</span>
            {thresholdDirty && (
              <button
                onClick={() => saveMutation.mutate({ submission_anomaly_threshold: parsedThreshold / 100 })}
                disabled={!thresholdValid || saveMutation.isPending}
                className="ml-2 px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>
      {saveMutation.isSuccess && <p className="mt-2 text-sm text-green-600">Feature flags saved</p>}
    </div>
  );
}

// ============================================================================
// COMMANDS TAB (unchanged)
// ============================================================================

function CommandsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <span className="text-amber-500 text-xl">!</span>
          <div>
            <h3 className="font-semibold text-amber-800 mb-2">Important: Week Boundary Timing</h3>
            <p className="text-amber-700 text-sm mb-3">
              PowerMove uses ISO weeks that start on <strong>Monday at 08:00 UTC</strong>.
              Reports submitted before this time will be counted in the previous week's summary.
            </p>
            <div className="bg-white rounded-lg p-3 text-sm">
              <p className="font-medium text-gray-700 mb-1">Example:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Report submitted Monday 07:30 UTC → counted in <strong>Week N-1</strong></li>
                <li>Report submitted Monday 08:30 UTC → counted in <strong>Week N</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Slack Commands Reference</h2>

        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded flex items-center justify-center text-xs">1</span>
            Reporting
          </h3>
          <div className="space-y-2 text-sm pl-8">
            <CommandRow command="/powermove report" description="Submit a new report" />
            <CommandRow command="/powermove continue <id>" description="Add follow-up to existing report" />
            <CommandRow command="/powermove list" description="View your reports (last 3 weeks)" />
            <CommandRow command="/powermove edit <id>" description="Edit a report" />
            <CommandRow command="/powermove delete <id>" description="Delete a report" />
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-green-100 text-green-600 rounded flex items-center justify-center text-xs">2</span>
            Stats & Summaries
          </h3>
          <div className="space-y-2 text-sm pl-8">
            <CommandRow command="/powermove stats" description="View your submission statistics" />
            <CommandRow command="/powermove summary" description="Monthly impact summary" />
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded flex items-center justify-center text-xs">3</span>
            Manager Commands
          </h3>
          <div className="space-y-2 text-sm pl-8">
            <CommandRow command="/powermove summary team" description="Team summary (who reported/not)" />
            <CommandRow command="/powermove rollup send" description="Lock & send rollup to your manager" />
            <CommandRow command="/powermove ownership" description="View your ownership assignments" />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center text-xs">4</span>
            Admin
          </h3>
          <div className="space-y-2 text-sm pl-8">
            <CommandRow command="/powermove admin add-customer <name>" description="Add a new customer" />
            <CommandRow command="/powermove admin add-product <name>" description="Add a new product" />
            <CommandRow command="/powermove admin set-manager @user @mgr" description="Set reporting relationship" />
            <CommandRow command="/powermove admin assign-ownership @user customer|product <name>" description="Assign ownership" />
          </div>
        </div>
      </div>
    </div>
  );
}

function CommandRow({ command, description }: { command: string; description: string }) {
  return (
    <div className="flex items-start gap-4">
      <code className="flex-shrink-0 w-80 text-primary-600 bg-gray-50 px-2 py-1 rounded font-mono text-xs">{command}</code>
      <span className="text-gray-500">{description}</span>
    </div>
  );
}
